import { z } from 'zod';
import { isoDateSchema, languageSchema, localizedTextSchema, objectIdSchema, slugSchema, } from './common.js';
/* ───────────────────────── categories ───────────────────────── */
export const categoryResponseSchema = z.object({
    id: objectIdSchema,
    name: localizedTextSchema,
    slug: slugSchema,
    order: z.number().int(),
    active: z.boolean(),
    /** Live journeys filed under this category, drafts included. */
    journeyCount: z.number().int(),
    /** How many of those are published. */
    publishedCount: z.number().int(),
    createdAt: isoDateSchema,
});
export const createCategoryBodySchema = z.object({
    name: localizedTextSchema,
    slug: slugSchema,
    order: z.number().int().min(0).default(0),
});
export const updateCategoryBodySchema = z
    .object({
    name: localizedTextSchema.optional(),
    slug: slugSchema.optional(),
    order: z.number().int().min(0).optional(),
    active: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/* ───────────────────────── journeys ───────────────────────── */
/** 5 / 7 / 14 / 21. Never hard-code a duration anywhere else. */
export const JOURNEY_DURATIONS = [5, 7, 14, 21];
export const durationDaysSchema = z.union([
    z.literal(5),
    z.literal(7),
    z.literal(14),
    z.literal(21),
]);
export const dailyMinutesContentSchema = z.union([z.literal(5), z.literal(10), z.literal(15)]);
/**
 * What shape a journey is.
 *
 *   DAILY       the product's loop: a fixed number of days, earned one after another.
 *   COLLECTION  a library of episodes grouped as sections → sub-journeys → episodes, played
 *               in any order. Introduced 2026-10-17 for Thirukkural: three sections (அறம் ·
 *               பொருள் · இன்பம்), 133 adhikarams, 1330 kurals. Nothing locks; progress is
 *               how many episodes have been finished.
 *
 * A COLLECTION has no durationDays — the field is null — and never appears in the daily
 * catalogue, the Home shelves or the onboarding match. See collection.ts for its contract.
 */
export const JOURNEY_KINDS = ['DAILY', 'COLLECTION'];
export const journeyKindSchema = z.enum(JOURNEY_KINDS);
export const journeyResponseSchema = z.object({
    id: objectIdSchema,
    contentId: objectIdSchema,
    language: languageSchema,
    categoryId: objectIdSchema,
    /**
     * → goals.key. Required — the only input the matching engine has.
     *
     * A journey is tagged with EXACTLY ONE, enforced on the way in (see
     * `createJourneyBodySchema`). The field stays an array because that is what the indexes,
     * the `$in` matching query and the mobile contract are built on; making it a scalar
     * would be a wire-breaking change to buy nothing the constraint does not already give.
     */
    goalKeys: z.array(z.string().min(1)),
    title: z.string(),
    description: z.string(),
    whatYouGain: z.array(z.string()),
    kind: journeyKindSchema,
    /** Null for a COLLECTION, which has no fixed length. */
    durationDays: durationDaysSchema.nullable(),
    dailyMinutes: dailyMinutesContentSchema,
    coverUrl: z.string().nullable(),
    /** What the browser measured when the cover was uploaded. Null for a pasted URL. */
    coverWidth: z.number().int().nullable(),
    coverHeight: z.number().int().nullable(),
    /** The Home hero, signed per read like the cover. */
    heroUrl: z.string().nullable(),
    heroWidth: z.number().int().nullable(),
    heroHeight: z.number().int().nullable(),
    /**
     * Editorially curated, not per-user. These drive Home's shelves: the portal decides what
     * is featured, the same way it decides what is published. There is no user-side favourite
     * in the MVP — CLAUDE.md §9 puts saved/bookmarked journeys out of scope.
     */
    isFavourite: z.boolean(),
    isTopChoice: z.boolean(),
    isPremium: z.boolean(),
    published: z.boolean(),
    publishedAt: isoDateSchema.nullable(),
    /** How many day rows exist so far — durationDays is the target. Always 0 for a COLLECTION. */
    dayCount: z.number().int(),
    /** COLLECTION only; both 0 for a DAILY journey. */
    sectionCount: z.number().int(),
    episodeCount: z.number().int(),
    createdAt: isoDateSchema,
});
export const createJourneyBodySchema = z
    .object({
    /**
     * Optional: the portal no longer asks for a category, so the API assigns the default
     * one. The concept is retired from the UI, not from the data — every journey still has
     * a categoryId, so bringing it back is a screen, not a migration.
     */
    categoryId: objectIdSchema.optional(),
    goalKeys: z.array(z.string().min(1)).length(1, 'Pick exactly one category'),
    title: z.string().trim().min(1).max(140),
    description: z.string().trim().min(1).max(2000),
    whatYouGain: z.array(z.string().trim().min(1).max(140)).max(8).default([]),
    kind: journeyKindSchema.default('DAILY'),
    /** Required for a DAILY journey, and refused for a COLLECTION — see the refinement. */
    durationDays: durationDaysSchema.optional(),
    dailyMinutes: dailyMinutesContentSchema,
    coverUrl: z.string().url().optional(),
    isPremium: z.boolean().default(false),
    language: languageSchema.default('en'),
})
    .refine((v) => v.kind !== 'DAILY' || v.durationDays !== undefined, {
    message: 'A daily journey needs a length',
    path: ['durationDays'],
})
    .refine((v) => v.kind !== 'COLLECTION' || v.durationDays === undefined, {
    message: 'A collection has no fixed length',
    path: ['durationDays'],
});
export const updateJourneyBodySchema = z
    .object({
    categoryId: objectIdSchema.optional(),
    goalKeys: z.array(z.string().min(1)).length(1, 'Pick exactly one category').optional(),
    title: z.string().trim().min(1).max(140).optional(),
    description: z.string().trim().min(1).max(2000).optional(),
    whatYouGain: z.array(z.string().trim().min(1).max(140)).max(8).optional(),
    durationDays: durationDaysSchema.optional(),
    dailyMinutes: dailyMinutesContentSchema.optional(),
    coverUrl: z.string().url().nullable().optional(),
    isPremium: z.boolean().optional(),
    isFavourite: z.boolean().optional(),
    isTopChoice: z.boolean().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
export const listJourneysQuerySchema = z.object({
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    categoryId: objectIdSchema.optional(),
    published: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
});
/* ───────────────────── publish readiness ───────────────────── */
export const PUBLISH_ISSUE_CODES = [
    'NO_GOALS',
    'DAY_COUNT_MISMATCH',
    'DAY_MISSING_ACTIVITY',
    'DAY_MISSING_STORY',
    'STORY_MISSING_AUDIO',
    'STORY_MISSING_DURATION',
    // COLLECTION journeys
    'NO_SECTIONS',
    'NO_READY_EPISODES',
    // Kept for older clients; no longer raised. A collection publishes with what is recorded
    // (decided 2026-10-17): unrecorded episodes are simply not shown to the app yet.
    'SECTION_EMPTY',
    'SUB_JOURNEY_EMPTY',
    'EPISODE_MISSING_AUDIO',
    'EPISODE_MISSING_DURATION',
];
export const publishIssueSchema = z.object({
    code: z.enum(PUBLISH_ISSUE_CODES),
    message: z.string(),
    /** DAILY journeys: which day. Null for journey-level and COLLECTION issues. */
    dayNumber: z.number().int().nullable(),
    /** COLLECTION journeys: which episode. Null otherwise. */
    episodeNumber: z.number().int().nullable().default(null),
});
export const publishCheckResponseSchema = z.object({
    publishable: z.boolean(),
    issues: z.array(publishIssueSchema),
});
/* ───────────────────────── stories ───────────────────────── */
export const storyResponseSchema = z.object({
    id: objectIdSchema,
    contentId: objectIdSchema,
    language: languageSchema,
    /** Null for an episode's story — those hang off a collection episode, not a day. */
    activityId: objectIdSchema.nullable(),
    title: z.string(),
    description: z.string(),
    /** GCS object path in a private bucket. Never a public URL. */
    audioPath: z.string().nullable(),
    durationSec: z.number().int(),
    sizeBytes: z.number().int(),
    hasAudio: z.boolean(),
    /**
     * A short-lived signed GET for the banner, resolved per read — the bucket is private, so
     * this cannot be stored and handed out later. Null when no banner has been uploaded.
     */
    bannerUrl: z.string().nullable(),
    /** What the browser measured, so the portal can warn about a badly cropped banner. */
    bannerWidth: z.number().int().nullable(),
    bannerHeight: z.number().int().nullable(),
    hasBanner: z.boolean(),
    createdAt: isoDateSchema,
});
export const updateStoryBodySchema = z
    .object({
    title: z.string().trim().min(1).max(140).optional(),
    description: z.string().trim().max(2000).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to update' });
/* ───────────────────────── days ───────────────────────── */
export const journeyDayResponseSchema = z.object({
    id: objectIdSchema,
    journeyId: objectIdSchema,
    dayNumber: z.number().int(),
    title: z.string(),
    activity: z.object({
        id: objectIdSchema,
        type: z.literal('STORY'),
        order: z.number().int(),
        story: storyResponseSchema.nullable(),
    }),
    createdAt: isoDateSchema,
});
/**
 * Creating a day also creates its single activity and a draft story, because the MVP has
 * exactly one story per day. One call instead of three, and no half-built days.
 */
export const createJourneyDayBodySchema = z.object({
    title: z.string().trim().min(1).max(140),
    storyTitle: z.string().trim().min(1).max(140).optional(),
    storyDescription: z.string().trim().max(2000).default(''),
});
export const updateJourneyDayBodySchema = z.object({
    title: z.string().trim().min(1).max(140),
});
/** Full ordered list of day ids. The server renumbers dayNumber to 1..N from this. */
export const reorderDaysBodySchema = z.object({
    dayIds: z.array(objectIdSchema).min(1),
});
/* ───────────────────────── upload ───────────────────────── */
export const AUDIO_CONTENT_TYPES = ['audio/mpeg', 'audio/mp4', 'audio/aac', 'audio/wav'];
export const audioContentTypeSchema = z.enum(AUDIO_CONTENT_TYPES);
/** Hard ceiling. A sub-10-minute story at 128 kbps is ~9 MB, so this is generous. */
export const MAX_AUDIO_BYTES = 52_428_800;
export const uploadUrlBodySchema = z.object({
    contentType: audioContentTypeSchema,
    sizeBytes: z.number().int().positive().max(MAX_AUDIO_BYTES),
});
export const uploadUrlResponseSchema = z.object({
    /**
     * Where to PUT the bytes. ABSOLUTE for a bucket, which lives on another host; RELATIVE
     * (`/uploads/…`) when the API stores the file itself, because only the client knows
     * which host it reached the API through. Resolve it against your own API base.
     */
    uploadUrl: z.string().min(1),
    objectPath: z.string(),
    expiresAt: isoDateSchema,
    /** Must be sent verbatim on the PUT — they are pinned into the signature. */
    headers: z.record(z.string()),
});
/* ───────────────────────── cover images ───────────────────────── */
export const IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const imageContentTypeSchema = z.enum(IMAGE_CONTENT_TYPES);
/**
 * 5 MB. Far more than a correctly exported cover needs — the recommendation is 150-300 KB
 * — but loose enough to absorb an honest mistake rather than a catastrophic one.
 */
export const MAX_IMAGE_BYTES = 5_242_880;
/**
 * What a journey cover should be, and why.
 *
 * The preview hero (artboard 4b) is 342 x 170 pt, and because every dimension scales with
 * the screen's width that ratio is IDENTICAL on every phone — an image at exactly 2:1
 * never crops anywhere. 1200 px wide covers a 440 pt phone at @3x with room over.
 *
 * The lower part of the hero is deliberately buried: the scrim runs to 96% opaque by 72%
 * down, and the title bar sits on top of that. Anything that matters has to live in the
 * top 60%, or it will be behind the journey's own name.
 */
/*
 * Both screens that show a cover display it at 2:1 — the journey preview, and the journey
 * detail panel since the 2d redesign (2026-09-22). The square crop the detail used to apply
 * is gone, so the only rule for artwork is to keep the subject clear of the scrim.
 */
export const COVER_IMAGE = {
    width: 1200,
    height: 600,
    aspect: 2,
    /** How far an upload may sit from 2:1 before it will visibly crop. */
    aspectTolerance: 0.06,
    /** Below this it will look soft on a @3x screen. */
    minWidth: 800,
    /**
     * The fraction of the image covered by chrome. Zero since 2026-09-27: the preview screen
     * used to lay its title bar over the bottom 40% behind a scrim, and now places it under
     * the panel instead — which is what the detail screen always did. Both screens show the
     * whole cover, so there is no longer a region to compose around.
     */
    obscuredFromTop: 0,
};
/**
 * Story banner art — the 330 x 300pt slot on the player (artboard 3a), and the thumbnail
 * a day row can carry.
 *
 * Square, because that is how track artwork is thought about everywhere and it survives
 * every crop the app asks of it: the player's slightly-landscape slot trims about 9% off
 * the top and bottom, a square thumbnail trims nothing. The player lays a scrim from 55%
 * down, so a subject in the lower half is lost behind the title and controls.
 */
export const STORY_BANNER = {
    width: 1080,
    height: 1080,
    aspect: 1,
    aspectTolerance: 0.06,
    minWidth: 720,
    obscuredFromTop: 0.55,
};
/**
 * A profile photo — the 62pt circle on artboard 6a.
 *
 * Square, because it is only ever shown in a circle: any other ratio loses its edges to the
 * crop whichever way it is framed. Modest at 512 because it renders at 62pt and the ring
 * around it hides the outermost pixels anyway.
 */
export const PROFILE_PHOTO = {
    width: 512,
    height: 512,
    aspect: 1,
    aspectTolerance: 0.06,
    minWidth: 256,
    obscuredFromTop: 0,
};
/**
 * The Home hero — the full-bleed 390 x 282pt slot behind the greeting on artboard 2a.
 *
 * Exactly 3x the slot, so it lands pixel-for-pixel on a @3x phone with no resampling.
 *
 * Unlike the cover and the banner, what buries this one is on the LEFT, not the top: 2a
 * lays a horizontal scrim that is near-opaque at the left edge and clear by 70% across,
 * because the headline and the streak pill sit over it. A subject on the left disappears.
 * Hence `obscuredFromLeft` rather than `obscuredFromTop` — same idea, different axis.
 */
export const HOME_HERO = {
    width: 1170,
    height: 846,
    aspect: 1170 / 846,
    aspectTolerance: 0.06,
    minWidth: 780,
    obscuredFromTop: 0,
    /** The fraction of the width the headline scrim covers. Keep the subject right of this. */
    obscuredFromLeft: 0.7,
};
/**
 * Step 1 of any image upload. Covers and story banners differ in where they land and what
 * shape they should be, not in how the bytes get there — one schema, both flows.
 */
export const imageUploadUrlBodySchema = z.object({
    contentType: imageContentTypeSchema,
    sizeBytes: z.number().int().positive().max(MAX_IMAGE_BYTES),
});
/** The journey cover's step 1. Kept as a name because that is what the portal calls it. */
export const coverUploadUrlBodySchema = imageUploadUrlBodySchema;
/**
 * Finalize carries the dimensions the BROWSER measured, the way audio finalize carries the
 * duration it decoded: the API never opens the file, so the only honest source for what
 * landed is the client that produced it.
 */
export const finalizeImageBodySchema = z.object({
    width: z.number().int().positive().max(10_000),
    height: z.number().int().positive().max(10_000),
});
export const finalizeCoverBodySchema = finalizeImageBodySchema;
export const finalizeUploadBodySchema = z.object({
    durationSec: z.number().int().positive().max(7200),
});
/* ─────────────────── what the app sees ─────────────────── */
/**
 * The mobile-facing shape of a journey. Deliberately smaller than JourneyResponse: the
 * app has no business knowing about contentId, publish state or how many days are built.
 */
export const journeySummarySchema = z.object({
    id: objectIdSchema,
    categoryId: objectIdSchema,
    goalKeys: z.array(z.string()),
    title: z.string(),
    description: z.string(),
    whatYouGain: z.array(z.string()),
    kind: journeyKindSchema,
    /** Null for a COLLECTION. The app shows an episode count instead of a day count. */
    durationDays: durationDaysSchema.nullable(),
    dailyMinutes: dailyMinutesContentSchema,
    coverUrl: z.string().nullable(),
    /**
     * The Home hero. Null falls Home back to the cover, and then to the goal's colour —
     * a journey without hero art must still render a Home screen.
     */
    heroUrl: z.string().nullable(),
    isPremium: z.boolean(),
});
/* ─────────────────── playback ─────────────────── */
/**
 * A short-lived link to the audio. The same shape from both storage drivers, so the
 * portal's player does not know or care where the file actually lives.
 */
export const storyPlaybackResponseSchema = z.object({
    storyId: objectIdSchema,
    title: z.string(),
    /** Absolute for a bucket, relative for API-hosted audio. See uploadUrlResponseSchema. */
    url: z.string().min(1),
    expiresAt: isoDateSchema,
    durationSec: z.number().int(),
});
/* ─────────────────── home ─────────────────── */
/**
 * What Home renders. It answers exactly one question — "what should I do today?" — so the
 * three-way branch is resolved on the server and the app renders one payload.
 *
 *   CONTINUE       today's journey is unfinished; pick it up (arrives with the engine)
 *   RECOMMENDATION nothing active, here is what to start
 *   EMPTY          the catalogue has nothing to offer this user yet
 */
export const todayStateSchema = z.enum(['CONTINUE', 'RECOMMENDATION', 'EMPTY']);
/** The one story Home offers to play. Null when there is nothing in progress. */
export const todayFocusSchema = z.object({
    storyId: objectIdSchema,
    activityId: objectIdSchema,
    dayNumber: z.number().int(),
    title: z.string(),
    durationSec: z.number().int(),
    /** So the card can say "12 min left" rather than only the total. */
    positionSec: z.number().int(),
    listenedSec: z.number().int(),
    /** This story's own art. Null falls the card back to the goal's colour and emoji. */
    bannerUrl: z.string().nullable(),
});
/**
 * One card on a Home shelf (artboard 2a). Four to a row, 92pt tall.
 *
 * Deliberately flatter than a JourneySummary: a shelf renders a picture, two lines and a
 * caption, and sending the whole journey for each of twelve cards would be most of a
 * catalogue page per Home load.
 */
export const homeShelfCardSchema = z.object({
    /**
     * The whole summary, not a flattened card.
     *
     * Tapping a shelf card opens the journey preview, and that screen takes a summary rather
     * than fetching one — so a flat card would force either a round trip per tap or a second
     * endpoint. This mirrors `catalogueItemSchema`, which the library already uses for the
     * identical job. The art is `heroUrl` falling back to `coverUrl`.
     */
    journey: journeySummarySchema,
    totalDays: z.number().int(),
    /**
     * The art a shelf tile shows: the first day's STORY BANNER, falling back to the home hero
     * and then the cover.
     *
     * Banners win because a tile is 83.5 x 92pt — very nearly square, which is the banner's
     * own shape, so it loses about 5% to the crop where a 1.38:1 hero loses 17% and a 2:1
     * cover loses 27%. Worse, a hero is composed with its subject pushed RIGHT to clear the
     * headline scrim, and a tile crops both edges towards the centre — so the hero's correct
     * composition is the wrong one here.
     *
     * Resolved server-side: which image represents a journey is a content decision, and
     * putting the chain here keeps all three clients agreeing on it.
     */
    tileUrl: z.string().nullable(),
    /** Already in this user's list — open the day list, not the pitch. */
    started: z.boolean(),
    /**
     * Not yet published. The card shows the lock and the "Coming Soon" chip, and tapping it
     * must not open anything — there is nothing behind it to open.
     */
    comingSoon: z.boolean(),
});
/**
 * Home's three shelves, curated in the portal.
 *
 * They ride in the today payload rather than having endpoints of their own: Home has to
 * render in one round trip, and three more requests on app open is three more things that
 * can be slow or fail on an Indian mobile connection.
 */
export const homeShelvesSchema = z.object({
    favourites: z.array(homeShelfCardSchema),
    topChoice: z.array(homeShelfCardSchema),
    comingSoon: z.array(homeShelfCardSchema),
});
/**
 * A whole shelf, for Home's "See All" (wired 2026-10-20 — it was a label before).
 *
 * The today payload carries only the first few of each shelf; this returns the lot.
 * Not paged: a shelf is curated by hand in the portal and never long.
 */
export const homeShelfKeySchema = z.enum(['favourites', 'topChoice', 'comingSoon']);
export const homeShelfParamsSchema = z.object({ key: homeShelfKeySchema });
export const homeShelfResponseSchema = z.object({
    key: homeShelfKeySchema,
    items: z.array(homeShelfCardSchema),
});
export const todayResponseSchema = z.object({
    state: todayStateSchema,
    /** Derived against today in the user's own timezone, never stored and read back blindly. */
    streak: z.number().int(),
    greetingName: z.string().nullable(),
    journey: journeySummarySchema.nullable(),
    /**
     * Set only on CONTINUE. On RECOMMENDATION the journey is one the user has not started,
     * so it has no progress of its own and these stay null — the app must not render a
     * progress bar for something that has not begun.
     */
    userJourneyId: objectIdSchema.nullable(),
    currentDay: z.number().int().nullable(),
    totalDays: z.number().int().nullable(),
    progressPct: z.number().int().min(0).max(100).nullable(),
    focus: todayFocusSchema.nullable(),
    /** The three curated shelves below the hero. Any of them may be empty. */
    shelves: homeShelvesSchema,
    /** Home's three-stat strip. Real from the first day rather than hard-coded zeroes. */
    journeysActive: z.number().int(),
    minutesToday: z.number().int(),
});
//# sourceMappingURL=content.js.map